<template lang="pug">
.edit-area
    label {{ $t(`${baseTranslationPath}.select_new_rule`)}}
    aiq-select(v-model="selectedRule" :placeholder="$t(`${baseTranslationPath}.select_new_rule_placeholder`)" value-key="id" @change="ruleChanged" :clearable="false")
        aiq-option(v-for="item in rules" :key="item.id" :label="item.name" :value="item")
    .field-group(v-if="selectedRulePayload ")
        .stages-title {{ $t(`${baseTranslationPath}.stages`) }}
        .group-item(v-for="(stage, index) in selectedRulePayload.stages" :key="stage.name")
            .field-container
                span.stage-name {{ stage.name }}
                .field-container.full-width.is-required
                    label {{ $t('settings_tab.smart_drive_tab.smart_control.select_agent_team', {min: stage.min_assignee_count}) }}
                    aiq-select(v-model="stage.assignees"
                                :max-length="250"
                                value-key="uniqueKey"
                                multiple
                                :reserve-keyword="false")
                        aiq-option(v-for="item in agentsAndTeams",
                            :key="item.uniqueKey",
                            :label="item.name",
                            :value="item")
        .field-container.full-width.padding-top
            label {{ $t(`${baseTranslationPath}.doc_owner`) }}
            aiq-input(v-model="selectedRulePayload.owner"
                        :placeholder="$t(`${baseTranslationPath}.doc_owner`)"
                        :max-length="250"
                        )
        .field-container.full-width.padding-top
            label {{ $t(`${baseTranslationPath}.source_url`) }}
            aiq-input(v-model="selectedRulePayload.source_url"
                        :placeholder="$t(`${baseTranslationPath}.source_url`)"
                        :max-length="250"
                        type="url"
                        )
        .field-container.full-width.padding-top
            label {{ $t('settings_tab.smart_drive_tab.smart_control.notify') }}
                aiq-tooltip(effect="dark"
                    :show-after="1000"
                    :content="$t('settings_tab.smart_drive_tab.smart_control.email_tooltip')")
                    fa-icon.question-mark(:icon="['fas', 'circle-question']")
            InputTag(@input="enterEmail"
                    @update-tag="selectedRulePayload.external_emails = $event"
                    :tagValue="selectedRulePayload.external_emails"
                    :placeholder="$t('settings_tab.smart_drive_tab.smart_control.enter_email')"
                    )
        .field-container.full-width.padding-top
            label {{ $t('settings_tab.smart_drive_tab.smart_control.expiry') }}
            aiq-input(v-model="selectedRulePayload.frequency_cycle"
                        min="1"
                        :max-length="250"
                        type="number")
        .field-container.full-width.padding-top
            label {{ $t(`${baseTranslationPath}.remove_title`) }}
                aiq-tooltip(effect="dark"
                    :show-after="1000"
                    :content="$t(`${baseTranslationPath}.remove_tooltip`)")
                    fa-icon.question-mark(:icon="['fas', 'circle-question']")
            .remove-btn
                aiq-button(type="danger" class="group-btn" @click="removeFromSmartCtrl") {{ $t(`global.delete`) }}
    .btns.padding-top
        aiq-button(type="primary" :disabled="!canSubmit" class="group-btn" @click="updateRecord") {{ $t('global.save') }}
        aiq-button(class="group-btn" @click="handleEditModalBeforeClose") {{ $t('global.cancel') }}
</template>

<script>
import InputTag from '@/components/InputTag.vue';
import get from 'lodash/get';
import CloneDeep from 'lodash/cloneDeep';
import { confirmBeforeDialogClose } from '@/libs';
import isEqual from 'lodash/isEqual';
import { validateEmail } from '@/libs/validationUtils';
import { checkIfRuleSatisfied } from '../../common/util';

export default {
  name: 'SmartControlEdit',
  components: {
    InputTag,
  },
  props: {
    smartControlRecord: {
      type: Object,
      required: true,
    },
    rules: {
      type: Array,
      required: true,
    },
    agentsAndTeams: {
      type: Array,
      required: true,
    },
  },
  emits: ['close', 'update-doc', 'remove'],
  computed: {
    canSubmit() {
      if (!this.selectedRulePayload) {
        return false;
      }
      if (!this.validateRule()) {
        return false;
      }
      if (isEqual(this.selectedRulePayload, this.selectedRulePayloadClone)) {
        return false;
      }
      if (this.selectedRulePayload.frequency_cycle === null || this.selectedRulePayload.frequency_cycle < 1) {
        return false;
      }
      return true;
    },
  },
  data() {
    return {
      selectedRule: null,
      selectedRulePayloadClone: {},
      selectedRulePayload: {
        stages: [],
        external_emails: [],
        owner: '',
        frequency_cycle: null,
        source_url: '',
      },
      baseTranslationPath:
        'settings_tab.admin_tab.smart_control.control_list_manage',
    };
  },
  async mounted() {
    this.preparePayload();
  },
  methods: {
    closeEditEditor() {
      this.$emit('close');
    },
    ruleChanged(value) {
      const currentRuleId = get(
        this.smartControlRecord,
        'current_stage.rule_id',
      );
      const selectedRuleId = get(value, 'id');
      this.syncRulePayload(currentRuleId, selectedRuleId);
      this.selectedRule = value;
    },
    async confirmDelete() {
      try {
        await this.$aiq.confirm(
          this.$t(`${this.baseTranslationPath}.remove_doc`),
          this.$t(`${this.baseTranslationPath}.remove_doc_confirm`),
        );
        return true;
      } catch {
        return false;
      }
    },
    async removeFromSmartCtrl() {
      const canRemove = await this.confirmDelete();
      if (!canRemove) {
        return;
      }
      this.$emit('remove');
    },
    handleEditModalBeforeClose() {
      if (isEqual(this.selectedRulePayload, this.selectedRulePayloadClone)) {
        this.closeEditEditor();
        return;
      }
      confirmBeforeDialogClose(this, this.closeEditEditor);
    },
    syncRulePayload(currentRuleId, selectedRuleId) {
      if (currentRuleId !== selectedRuleId) {
        this.prepareEmptyPayload(selectedRuleId);
      } else {
        this.preparePayload();
      }
    },

    prepareEmptyPayload(rule_id) {
      const rule = this.rules.find((rule) => rule.id === rule_id);
      const stages = get(rule, 'payload.stages', []);

      const payload = stages.map((stage) => {
        const default_assignees = get(stage, 'default_assignees', []);
        const mappedDefaultAssignees = default_assignees.map((assignee) => {
          return {
            ...assignee,
            uniqueKey: `${assignee.type}-${assignee.id}`,
          };
        });
        return {
          name: stage.name,
          min_assignee_count: stage.min_assignee_count,
          assignees: mappedDefaultAssignees,
          stage_id: get(stage, 'stage_id'),
        };
      });

      this.selectedRulePayload = {
        stages: payload,
        external_emails: get(
          this.smartControlRecord,
          'document.meta.smart_control.external_emails',
          [],
        ),
        frequency_cycle: get(
          this.smartControlRecord,
          'document.meta.smart_control.frequency_cycle',
          1,
        ),
        owner: get(
          this.smartControlRecord,
          'document.meta.smart_control.owner',
          '',
        ),
        source_url: get(
          this.smartControlRecord,
          'document.meta.smart_control.source_url',
          '',
        ),
      };
    },
    preparePayload() {
      const stages = CloneDeep(get(this.smartControlRecord, 'stages', []));
      const currentStage = get(this.smartControlRecord, 'current_stage');

      let selectedRule;

      if (!currentStage) {
        const rule_id = get(stages, '0.rule_id');
        selectedRule = this.rules.find((rule) => rule.id === rule_id);
      } else {
        selectedRule = this.rules.find(
          (rule) => rule.id === get(currentStage, 'rule_id'),
        );
      }
      this.selectedRule = selectedRule;

      if (!stages.length) return;

      const payload = stages.map((stage) => {
        const ruleStage = get(selectedRule, 'payload.stages', []).find(
          (s) => s.name.toLowerCase() === stage.name.toLowerCase(),
        );

        stage.assignees = stage.assignees.map((assignee) => {
          if (assignee.assignee_type === 'team') {
            return {
              type: 'team',
              id: assignee.id,
              name: assignee.name,
              uniqueKey: `team-${assignee.id}`,
            };
          } else if (assignee.assignee_type === 'agent') {
            return {
              type: 'agent',
              id: assignee.id,
              name: assignee.name,
              uniqueKey: `agent-${assignee.id}`,
            };
          }
        });
        return {
          name: stage.name,
          min_assignee_count: get(ruleStage, 'min_assignee_count', 1),
          stage_id: get(ruleStage, 'stage_id'),
          assignees: this.agentsAndTeams.filter((option) =>
            stage.assignees.some((a) => a.uniqueKey === option.uniqueKey),
          ),
        };
      });
      this.selectedRulePayload = {
        stages: payload,
        external_emails: get(
          this.smartControlRecord,
          'document.meta.smart_control.external_emails',
          [],
        ),
        frequency_cycle: get(
          this.smartControlRecord,
          'document.meta.smart_control.frequency_cycle',
          1,
        ),
        owner: get(
          this.smartControlRecord,
          'document.meta.smart_control.owner',
          '',
        ),
        source_url: get(
          this.smartControlRecord,
          'document.meta.smart_control.source_url',
          '',
        ),
      };
      this.selectedRulePayloadClone = CloneDeep(this.selectedRulePayload);
    },
    validateRule() {
      const stages = get(this.selectedRulePayload, 'stages', []);
      return checkIfRuleSatisfied(stages);
    },
    updateRecord() {
      if (!this.validateRule()) {
        this.$aiq.notify.error(
          this.$t(`${this.baseTranslationPath}.fill_all_fields`),
        );
        return;
      }

      const data = {
        name: get(this.selectedRule, 'name'),
        id: get(this.selectedRule, 'id'),
        delete_document_on_error: false,
        payload: {
          external_emails: get(
            this.selectedRulePayload,
            'external_emails',
            [],
          ),
          frequency_cycle: parseInt(
            get(this.selectedRulePayload, 'frequency_cycle', 1),
            10,
          ),
          owner: get(this.selectedRulePayload, 'owner', ''),
          source_url: get(this.selectedRulePayload, 'source_url', ''),
          //remove uniqueKey from assignees
          stages: this.selectedRulePayload.stages.map((stage) => {
            return {
              name: stage.name,
              min_assignee_count: stage.min_assignee_count,
              stage_id: stage.stage_id,
              assignees: stage.assignees.map((assignee) => {
                return {
                  type: assignee.type,
                  id: assignee.id,
                  name: assignee.name,
                };
              }),
            };
          }),
        },
      };

      this.$emit('update-doc', data);
      this.closeEditEditor();
    },

    enterEmail(email) {
      const normalizedEmail = String(email).trim().toLowerCase();

      if (!validateEmail(normalizedEmail)) {
        this.$aiq.notify.error(
          this.$t('settings_tab.smart_drive_tab.smart_control.invalid_email'),
        );
        return;
      }

      const external_emails = get(
        this.selectedRulePayload,
        'external_emails',
        [],
      );
      const isDuplicate = external_emails.some(
        (participant) =>
          String(participant).trim().toLowerCase() === normalizedEmail,
      );
      if (isDuplicate) {
        return;
      }

      this.selectedRulePayload.external_emails.push(normalizedEmail);
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-area {
    margin-top: 20px;
    margin-bottom: 70px;
}
.btns {
    display: flex;
    justify-content: flex-end;
}

.stages-title {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 10px;
    margin-top: 20px;
}

.group-item {
    margin-bottom: 20px;
    padding-left: 40px;
}
.padding-top {
    padding-top: 10px;
    padding-bottom: 10px;
}
.question-mark {
    cursor: pointer;
    padding-left: 10px;
}
</style>
